import { useState } from 'react';
import { FileText, Image, File, Loader2, FolderOpen } from 'lucide-react';
import type { IDocument } from '@/models/document';
import type { LucideIcon } from 'lucide-react';
import { savApi } from '../api/savApi';
import { getDocumentDownloadName } from '../utils/documentDownloadName';

interface SavDocumentGridProps {
  savId: number;
  documents: IDocument[];
  title: string;
  icon: LucideIcon;
  emptyMessage?: string;
}

function getFileIcon(doc: IDocument): LucideIcon {
  const name = (doc.fileName || doc.originalName || doc.label || '').toLowerCase();
  const ext = name.split('.').pop() || '';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return Image;
  if (['pdf', 'doc', 'docx'].includes(ext)) return FileText;
  return File;
}

function getDisplayName(doc: IDocument): string {
  return doc.label || doc.fileName || doc.originalName || `Document #${doc.id}`;
}

function formatDocDate(dateStr?: string): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function SavDocumentGrid({ savId, documents, title, icon: Icon, emptyMessage }: SavDocumentGridProps) {
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const handleDownload = async (doc: IDocument) => {
    setDownloadingId(doc.id);
    try {
      await savApi.downloadDocument(savId, doc.id, getDocumentDownloadName(doc));
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-p-4">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
        <Icon className="tw-w-4 tw-h-4 tw-text-gray-500" />
        <span className="tw-text-sm tw-font-semibold tw-text-gray-700">{title}</span>
        {documents.length > 0 && (
          <span className="tw-text-xs tw-text-gray-400">({documents.length})</span>
        )}
      </div>

      {/* Grid */}
      {documents.length === 0 ? (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-8">
          <FolderOpen className="tw-w-12 tw-h-12 tw-text-gray-300 tw-mb-2" />
          <p className="tw-text-sm tw-text-gray-400 tw-mb-0">{emptyMessage || 'Aucun document'}</p>
        </div>
      ) : (
        <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 tw-gap-3">
          {documents.map((doc) => {
            const DocIcon = getFileIcon(doc);
            const displayName = getDisplayName(doc);
            const isDownloading = downloadingId === doc.id;

            return (
              <button
                key={doc.id}
                onClick={() => handleDownload(doc)}
                disabled={isDownloading}
                className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-p-3 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 hover:tw-bg-affinicar-lighter hover:tw-border-affinicar-primary/30 tw-transition-colors tw-cursor-pointer tw-text-center disabled:tw-opacity-50 disabled:tw-cursor-wait"
              >
                {isDownloading ? (
                  <Loader2 className="tw-w-8 tw-h-8 sm:tw-w-10 sm:tw-h-10 tw-text-affinicar-primary tw-animate-spin" />
                ) : (
                  <DocIcon className="tw-w-8 tw-h-8 sm:tw-w-10 sm:tw-h-10 tw-text-affinicar-primary" />
                )}
                <span className="tw-text-xs tw-font-medium tw-text-gray-700 tw-truncate tw-w-full" title={displayName}>
                  {displayName}
                </span>
                {(doc.dateCreation || doc.createdAt) && (
                  <span className="tw-text-[10px] tw-text-gray-400">
                    {formatDocDate(doc.dateCreation || doc.createdAt)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
